import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  GraduationCap,
  Layers,
  BookMarked,
  Network,
  PencilLine,
  Trash2,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const SubjectsGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Subjects Guide"
          title="Subjects & Streams Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  Subjects &amp; Streams Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Subjects &amp; Streams Module!
                  </p>
                  <p className="gml-welcome-text">
                    This guide will help you manage the hierarchical structure of
                    classes, streams, and subjects in your institution. Use it
                    anytime you need a quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Class Management */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <GraduationCap className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Class Management</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        Add, edit, and delete academic classes (6–12)
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        View all streams within each class
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Expand / collapse class details with one click
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Stream Management */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <Layers className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Stream Management</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Create streams within classes (e.g., Science, Arts)
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Manage streams not assigned to any class (N/A)
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        View total paces and credits per stream
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Subject Management */}
                <div className="gml-feature-card gml-feature-card--yellow">
                  <div className="gml-card-head gml-card-head--yellow">
                    <BookMarked className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Subject Management</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">1</span>
                        Add subjects to streams with pace and credit details
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">2</span>
                        Track pace numbers (single or ranges)
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">3</span>
                        Automatic calculation of stream totals
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Navigating the Structure */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Network className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Navigating the Structure</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Hierarchical View</p>
                    <ul className="gml-detail-list">
                      <li>The system displays classes → streams → subjects in an expandable tree structure</li>
                      <li>Click the expand icon to open a section and the collapse icon to close it</li>
                      <li>Each level shows relevant totals (paces, credits) at a glance</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Sorting Options</p>
                    <ul className="gml-detail-list">
                      <li><strong>Class:</strong> Sort numerically (6–12 or 12–6)</li>
                      <li><strong>Stream:</strong> Sort alphabetically (A–Z or Z–A)</li>
                      <li><strong>Subject:</strong> Sort alphabetically (A–Z or Z–A)</li>
                      <li><strong>Paces:</strong> Sort by number of paces (ascending / descending)</li>
                      <li><strong>Credits:</strong> Sort by credit value (ascending / descending)</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Search Functionality</p>
                    <ul className="gml-detail-list">
                      <li>Search across all fields — class names, stream names, subject names, and more</li>
                      <li>Matching entries are highlighted throughout the hierarchy</li>
                      <li>Non-matching items are automatically filtered out of view</li>
                    </ul>
                  </div>
                </div>

                {/* Adding and Editing Items */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <PencilLine className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Adding and Editing Items</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Adding New Items</p>
                    <ul className="gml-detail-list">
                      <li><strong>Add Class:</strong> Use the "Add Class" button (numeric value 6–12)</li>
                      <li><strong>Add Stream:</strong> Click the "+ Stream" button within a class</li>
                      <li><strong>Add Subject:</strong> Click the "+ Subject" button within a stream</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Editing Items</p>
                    <ul className="gml-detail-list">
                      <li>Click the edit (pencil) button to edit any item</li>
                      <li>Modals open with current values pre-filled for convenience</li>
                      <li>Changes propagate correctly through the hierarchy</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Validation Rules</p>
                    <ul className="gml-detail-list">
                      <li><strong>Class:</strong> Must be a number between 6 and 12</li>
                      <li><strong>Stream / Subject:</strong> Must contain only letters and spaces</li>
                      <li><strong>Paces:</strong> Must be a number between 1 and 50</li>
                      <li><strong>Pace Numbers:</strong> Single number or range (e.g., 1097–1108)</li>
                      <li><strong>Credits:</strong> Must be a number between 1 and 50</li>
                    </ul>
                  </div>
                </div>

                {/* Deleting Items */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <Trash2 className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Deleting Items</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Deletion Hierarchy</p>
                    <ul className="gml-detail-list">
                      <li><strong>Deleting a Class:</strong> Also deletes all streams and subjects within it</li>
                      <li><strong>Deleting a Stream:</strong> Also deletes all subjects within that stream</li>
                      <li><strong>Deleting a Subject:</strong> Only removes that specific subject</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Safety Measures</p>
                    <ul className="gml-detail-list">
                      <li>A confirmation dialog appears before any deletion</li>
                      <li>Clear warning about cascading deletions is shown</li>
                      <li>No undo functionality — deletions are permanent</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Visual Indicators</p>
                    <ul className="gml-detail-list">
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Safe</span>
                        — Deleting a single subject only
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Cascading</span>
                        — Deleting a class or stream removes nested items
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Best Practices</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Plan your class and stream structure before adding items.',
                    'Use consistent naming conventions for streams and subjects.',
                    'Double-check pace numbers and credits when adding subjects.',
                    'Review totals at the stream level to ensure accuracy.',
                    'Use the search function to quickly locate specific items.',
                    'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default SubjectsGuideModal;